// DOCS-KB: a doc's writes shared by the Docs rows and the page header: archive, restore
// (`pages.unarchive`), delete (`pages.delete`, a soft delete; editors only: the server's 403,
// "Only editors can delete", is the shell's error toast) and Copy link. Each refetches every Work
// list (the tree, the Docs list, FAVORITES) once.
import { newIdempotencyKey } from "@companyos/sdk";
import { toast } from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { invalidateWork, workKeys } from "./work-queries";

type Doc = { id: string; title: string };

export function docUrl(slug: string, id: string) {
  return `${window.location.origin}/c/${slug}/work/p/${id}`;
}

export function copyDocLink(slug: string, id: string) {
  void navigator.clipboard?.writeText(docUrl(slug, id)).then(
    () => toast.add({ title: "Link copied" }),
    () => toast.add({ title: "The link could not be copied", type: "warning" }),
  );
}

export function useDocWrites(api: Api, company: CompanyAccess) {
  const co = company.id;
  const queryClient = useQueryClient();
  const refresh = () => invalidateWork(queryClient, co);
  const archive = useMutation({
    mutationFn: (d: Doc) =>
      api.client.pages.archive({ params: { co, id: d.id }, idempotencyKey: newIdempotencyKey() }),
    onSuccess: async (p) => {
      toast.add({ title: `${p.title} archived`, description: "Its pages moved up a level." });
      queryClient.setQueryData(workKeys.page(co, p.id), p);
      await refresh();
    },
  });
  const restore = useMutation({
    mutationFn: (d: Doc) =>
      api.client.pages.unarchive({
        params: { co, id: d.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: async (p) => {
      toast.add({ title: `${p.title} restored`, type: "success" });
      queryClient.setQueryData(workKeys.page(co, p.id), p);
      await refresh();
    },
  });
  const remove = useMutation({
    /** `leave`: runs once the delete succeeded, before the lists refetch (the page screen leaves). */
    mutationFn: async (d: Doc & { leave?: () => void }) => {
      await api.client.pages.delete({ params: { co, id: d.id } });
      d.leave?.();
      return d;
    },
    onSuccess: async (d) => {
      toast.add({ title: `${d.title} deleted` });
      queryClient.removeQueries({ queryKey: workKeys.page(co, d.id) });
      await refresh();
    },
  });
  return { archive, restore, remove };
}
